<template>
    <div v-if="!materials.length || materials.some(item => !hiddenMaterials.includes(item))" class="materials-list">
        <!-- Список выбранных материалов -->
        <div v-if="materials && materials.length" class="mb-3">
            <div v-for="(material, index) in materials" v-show="!hiddenMaterials.includes(material)" :key="getMaterialKey(material, index)"
                 class="border-bottom py-2">
                
                <!-- Первая строка: название, количество с единицей, кнопка удаления -->
                <div class="d-flex flex-column flex-md-row align-items-md-center mb-2">
                    <!-- Левая часть: название и badge -->
                    <div class="d-flex align-items-center flex-grow-1 mb-2 mb-md-0">
                        <EmployeeLink v-if="employeeCatalog && !isCustomMaterial(material)" :user-id="material.catalog_item_id || material.id"
                            :fallback="getMaterialName(material)" compact class="mr-2" />
                        <strong v-else-if="!editable" class="mr-2">{{ getMaterialName(material) }}</strong>
                      <b-form-input
                          v-else
                          v-model="localMaterials[index].item_name"
                          placeholder="Название материала"
                          size="sm"
                          class="flex-grow-1 mr-2"
                          :disabled="lockDirectoryNames && !isCustomMaterial(material)"
                          v-b-tooltip.hover
                          :title="lockDirectoryNames && !isCustomMaterial(material) ? 'Материал из справочника — наименование менять нельзя' : ''"
                          @input="onFieldChange(index, 'item_name')"
                      />
                      <b-badge v-if="isCustomMaterial(material)" variant="warning" class="mr-2">
                            Вручную
                        </b-badge>
                    </div>
                    
                    <!-- Правая часть: количество с единицей и кнопка удаления -->
                    <div class="d-flex align-items-center">
                        <b-input-group v-if="showQuantity" size="sm" class="mr-2" style="max-width: 150px;">
                            <b-form-input
                                :ref="`quantity-${index}`"
                                v-model.number="localMaterials[index].quantity"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0"
                                @focus="onQuantityFocus($event, index)"
                                @blur="onQuantityBlur(index)"
                                @input="onFieldChange(index, 'quantity')"
                            />
                            <b-input-group-append>
                                <b-input-group-text class="text-muted">
                                    {{ getMaterialUnit(material) }}
                                </b-input-group-text>
                            </b-input-group-append>
                        </b-input-group>
                        
                        <b-button 
                            v-if="showRemoveButton"
                            variant="outline-danger" 
                            size="sm" 
                            @click="removeMaterial(index)"
                            class="align-self-center"
                        >
                            <i class="fas fa-times"></i>
                        </b-button>
                    </div>
                </div>
                
                <!-- Вторая строка: примечания на всю ширину -->
                <AppTextarea
                    v-model="localMaterials[index].notes"
                    placeholder="Примечания к материалу (необязательно)"
                    size="sm"
                    class="w-100"
                    maxlength="1000"
                    @input="onFieldChange(index, 'notes')"
                />
                <slot name="item-extra" :material="material" :index="index"></slot>
            </div>
        </div>
        <div v-else class="text-muted">
            {{ emptyText }}
        </div>
    </div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
export default {
    name: 'MaterialsList',
    components: {EmployeeLink},
    props: {
        employeeCatalog: {type: Boolean, default: false},
        hiddenMaterials: {type: Array, default: () => []},
        // Массив материалов
        materials: {
            type: Array,
            required: true,
            default: () => []
        },
        // Можно ли редактировать название материала
        editable: {
            type: Boolean,
            default: false
        },
        // Показывать ли кнопку удаления
        showRemoveButton: {
            type: Boolean,
            default: true
        },
        lockDirectoryNames: {
          type: Boolean,
          default: false
        },
        showQuantity: {
            type: Boolean,
            default: true
        },
        emptyText: {
            type: String,
            default: 'Материалы не выбраны'
        }
    },
    data() {
        return {
            localMaterials: []
        };
    },
    watch: {
        materials: {
            handler(newMaterials) {
                // Создаем локальную копию для редактирования
                this.localMaterials = newMaterials.map(material => ({
                    ...material,
                    item_name: this.getMaterialName(material),
                    quantity: this.getMaterialQuantity(material),
                    unit: this.getMaterialUnit(material),
                    notes: this.getMaterialNotes(material)
                }));
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        /**
         * Получить ключ для материала (для v-for)
         */
        getMaterialKey(material, index) {
            return material.id || material._tempId || `material-${index}`;
        },
        
        /**
         * Получить название материала (поддержка разных форматов)
         */
        getMaterialName(material) {
            return material.item_name || material.name || '';
        },
        
        /**
         * Получить количество материала
         */
        getMaterialQuantity(material) {
            return material.quantity || 0;
        },
        
        /**
         * Получить единицу измерения материала
         */
        getMaterialUnit(material) {
            return material.unit || material.unit_of_measurement || 'шт';
        },
        
        /**
         * Получить примечания материала
         */
        getMaterialNotes(material) {
            return material.notes || '';
        },
        
        /**
         * Проверить, является ли материал кастомным
         */
        isCustomMaterial(material) {
            return material.isCustom === true || material.is_custom === true;
        },
        
        /**
         * Обработчик изменения поля материала
         */
        onFieldChange(index, field) {
            const material = this.localMaterials[index];
            const originalMaterial = this.materials[index];

            // запрет изменения имени для справочника
            if (
                field === 'item_name' &&
                this.lockDirectoryNames &&
                !this.isCustomMaterial(originalMaterial)
            ) {
              // вернем локальное значение к исходному, чтобы не было рассинхрона
              this.localMaterials[index].item_name = this.getMaterialName(originalMaterial);
              return;
            }
            
            // Для совместимости с разными форматами данных
            let updateValue = material[field];
            let updateField = field;
            
            // Определяем формат данных оригинала
            const hasName = originalMaterial.name !== undefined;
            const hasItemName = originalMaterial.item_name !== undefined;
            const hasUnit = originalMaterial.unit !== undefined;
            const hasUnitOfMeasurement = originalMaterial.unit_of_measurement !== undefined;
            
            // Если обновляется item_name, используем правильное поле
            if (field === 'item_name') {
                updateField = hasName ? 'name' : 'item_name';
            }
            
            // Если обновляется unit, используем правильное поле
            if (field === 'unit') {
                updateField = hasUnitOfMeasurement ? 'unit_of_measurement' : 'unit';
            }
            
            // Эмитим событие обновления
            this.$emit('update:material', {
                index,
                field: updateField,
                value: updateValue,
                material: {
                    ...originalMaterial,
                    [updateField]: updateValue,
                    // Также обновляем оба поля для совместимости
                    item_name: field === 'item_name' ? updateValue : (originalMaterial.item_name || originalMaterial.name || ''),
                    name: field === 'item_name' ? updateValue : (originalMaterial.name || originalMaterial.item_name || ''),
                    unit: field === 'unit' ? updateValue : (originalMaterial.unit || originalMaterial.unit_of_measurement || ''),
                    unit_of_measurement: field === 'unit' ? updateValue : (originalMaterial.unit_of_measurement || originalMaterial.unit || '')
                }
            });
        },
        
        /**
         * Обработчик фокуса на поле количества
         * Автоматически очищает поле если значение равно 0
         */
        onQuantityFocus(event, index) {
            const material = this.localMaterials[index];
            const value = material.quantity;
            if (value === 0 || value === '0' || value === null || value === '') {
                material.quantity = '';
                this.$nextTick(() => {
                    const input = event.target;
                    if (input && input.select) {
                        input.select();
                    }
                });
            } else {
                this.$nextTick(() => {
                    const input = event.target;
                    if (input && input.select) {
                        input.select();
                    }
                });
            }
        },
        
        /**
         * Обработчик потери фокуса на поле количества
         * Устанавливает 0 если поле пустое
         */
        onQuantityBlur(index) {
            const material = this.localMaterials[index];
            if (material.quantity === '' || material.quantity === null || material.quantity === undefined) {
                material.quantity = 0;
                this.onFieldChange(index, 'quantity');
            }
        },
        
        /**
         * Удалить материал
         */
        removeMaterial(index) {
            this.$emit('remove:material', index);
        }
    }
};
</script>

<style scoped>
.materials-list {
    width: 100%;
}

.materials-list .border-bottom {
    border-bottom: 1px solid #dee2e6 !important;
}

.materials-list .border-bottom:last-child {
    border-bottom: none !important;
}
</style>
